import { useEffect, useMemo } from "react";
import { useDependencyInjector } from "@whatslly/commons/di";
import { injectable } from "inversify";
import { makeObservable, observable, action } from "mobx";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import isEqual from "lodash/isEqual";

const noop = () => null;
@injectable()
export class UrlStore {
  initialized = false;
  searchParams?: ReturnType<typeof useSearchParams>[0];
  params: ReturnType<typeof useParams> = {};
  navigate: ReturnType<typeof useNavigate> = noop;
  setParams: ReturnType<typeof useSearchParams>[1] = noop;
  pathname: string = "";
  constructor() {
    makeObservable(this, {
      initialized: observable,
      searchParams: observable.ref,
      params: observable.ref,
      pathname: observable.ref,
      setup: action,
    });
  }

  setup(
    [searchParams, setParams]: ReturnType<typeof useSearchParams>,
    params: ReturnType<typeof useParams>,
    navigate: ReturnType<typeof useNavigate>,
    pathname: string
  ) {
    this.initialized = true;
    this.searchParams = searchParams;
    this.navigate = navigate;
    this.setParams = setParams;

    if (!isEqual(this.params, params)) {
      this.params = params;
    }

    if (!isEqual(this.pathname, pathname)) {
      this.pathname = pathname;
    }
  }

  setSearchParams(params: Record<string, string | string[] | undefined>) {
    Object.entries(params).forEach(([key, value]) => {
      if (!value?.length) {
        this.searchParams?.delete(key);
      } else if (Array.isArray(value)) {
        this.searchParams?.set(key, value.join(","));
      } else {
        this.searchParams?.set(key, value);
      }
    });

    this.setParams?.(this.searchParams ?? {});
  }

  resetChatGroupsParams(pathname: string) {
    const gotChatGroupsFilters = this.searchParams?.has("createdByMe") || this.searchParams?.has("groupsImIn");
    if (pathname === "/chat/groups" || !gotChatGroupsFilters) return;
    this.setSearchParams({
      createdByMe: undefined,
      groupsImIn: undefined,
    });
  }
}

export const useSetupUrlStore = () => {
  const urlStore = useDependencyInjector(UrlStore);

  const searchPrams = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const paramsSignature = useMemo(() => JSON.stringify(params), [params]);

  useEffect(() => {
    urlStore.setup(searchPrams, params, navigate, pathname);
    if (pathname !== "/chat/groups") urlStore.resetChatGroupsParams(pathname);
  }, [pathname, search, paramsSignature, navigate, searchPrams]);

  return urlStore.initialized;
};
